import './App.css';
import { useEffect, useState, useRef } from 'react';
import { useLoadItems } from './BskyLoader';
import { useSearchParams } from 'react-router';

const BREAKPOINTS = { 720: 1, 950: 2, 1600: 3, 1900: 4 };
const GUTTERS   = { 1: '8px', 2: '12px', 3: '14px', 4: '14px' };

function useColumnCount() {
  const [cols, setCols] = useState(1);
  useEffect(() => {
    function update() {
      const w = window.innerWidth;
      let result = 1;
      for (const [bp, c] of Object.entries(BREAKPOINTS).sort((a, b) => Number(a[0]) - Number(b[0]))) {
        if (w >= Number(bp)) result = c;
      }
      setCols(result);
    }
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return cols;
}

function Gallery({ setFullscreenImage }) {
  const [searchParams] = useSearchParams();
  const feed = searchParams.get("feed");
  const user = searchParams.get("user") || "pics.yernemm.xyz";
  const { loading, items, hasNextPage, error, loadMore } = useLoadItems({ user, feed });
  const columnCount = useColumnCount();
  const [columns, setColumns] = useState([]);

  // Aspect ratios stored in a ref so loads don't cause extra re-renders
  const aspectRatios = useRef({});
  const loadingSet  = useRef(new Set());
  const [ratioVersion, setRatioVersion] = useState(0);

  const sentinelRefs  = useRef([]);
  const loadingRef    = useRef(loading);
  const hasNextPageRef = useRef(hasNextPage);
  const loadMoreRef   = useRef(loadMore);

  useEffect(() => { loadingRef.current    = loading;     }, [loading]);
  useEffect(() => { hasNextPageRef.current = hasNextPage; }, [hasNextPage]);
  useEffect(() => { loadMoreRef.current   = loadMore;    }, [loadMore]);

  // Preload aspect ratios for items we haven't seen yet
  useEffect(() => {
    const toLoad = items.filter(
      item => !(item.src in aspectRatios.current) && !loadingSet.current.has(item.src)
    );
    if (toLoad.length === 0) return;
    toLoad.forEach(item => loadingSet.current.add(item.src));
    Promise.all(toLoad.map(item =>
      new Promise(resolve => {
        const img = new Image();
        img.onload  = () => resolve({ src: item.src, ratio: img.naturalHeight / img.naturalWidth });
        img.onerror = () => resolve({ src: item.src, ratio: 1 });
        img.src = item.thumb;
      })
    )).then(results => {
      results.forEach(({ src, ratio }) => { aspectRatios.current[src] = ratio; });
      setRatioVersion(v => v + 1);
    });
  }, [items]);

  // Recompute column assignment whenever ratios or column count change
  useEffect(() => {
    if (items.length === 0) { setColumns([]); return; }
    if (!items.every(item => item.src in aspectRatios.current)) return;

    const cols    = Array.from({ length: columnCount }, () => []);
    const heights = Array(columnCount).fill(0);

    for (const item of items) {
      const shortest = heights.indexOf(Math.min(...heights));
      cols[shortest].push(item);
      heights[shortest] += aspectRatios.current[item.src];
    }

    setColumns(cols);
  }, [items, ratioVersion, columnCount]);

  // Observe all column sentinels for infinite scroll
  useEffect(() => {
    if (error) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some(e => e.isIntersecting) && hasNextPageRef.current && !loadingRef.current) {
          loadMoreRef.current();
        }
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    sentinelRefs.current.forEach(el => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [columns, columnCount, hasNextPage, error]);

  const gap = GUTTERS[columnCount] ?? '14px';

  return (
    <div style={{ padding: '12px', display: 'flex', gap, alignItems: 'flex-start' }}>
      {Array.from({ length: columnCount }, (_, ci) => (
        <div key={ci} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap }}>
          {(columns[ci] || []).map(image => (
            <div className='gallery-image-container' key={image.src} onClick={() => setFullscreenImage(image)}>
              <img src={image.thumb} alt="" className='gallery-image' />
              <div className='gallery-image-overlay'><span>{image.text}</span></div>
            </div>
          ))}
          {hasNextPage && (
            <div ref={el => { sentinelRefs.current[ci] = el; }} className="gallery-sentinel">
              {loading && <div className="gallery-loading">Loading...</div>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default Gallery;
